import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Package,
  ArrowUpRight,
  Search,
  Check,
  X as XIcon,
  Filter,
  AlertOctagon,
  Download,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import Modal from "../components/Modal";
import VoiceTextarea from "../components/VoiceTextarea";
import { useAuth } from "../context/AuthContext";
import UnseenDot from "../components/UnseenDot";
import useUnseen from "../lib/useUnseen";
import useFocusOnMount from "../lib/useFocusOnMount";
import FromChatPill from "../components/chat/FromChatPill";
import useServerPdf from "../lib/useServerPdf";

const STATUSES = [
  { value: "pending_review", label: "Pending review", color: "#F59E0B" },
  { value: "quoted", label: "Quoted", color: "#06B6D4" },
  { value: "confirmed", label: "Confirmed", color: "#A78BFA" },
  { value: "fulfilled", label: "Fulfilled", color: "#10B981" },
  { value: "cancelled", label: "Cancelled", color: "#647087" },
];

export default function OrderRequestsPage() {
  const { user } = useAuth();
  const { orderId: deepLinkedOrderId } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEdit = ["admin", "manager", "owner", "rep"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([
        api.get("/order-requests"),
        api.get("/customers"),
      ]);
      setOrders(o.data || []);
      setCustomers(c.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("orders", orders);
  useEffect(() => { touch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useFocusOnMount({ ready: orders.length > 0, deps: [orders.length] });

  // Deep-link: /orders/:orderId → auto-open that order after load.
  useEffect(() => {
    if (!deepLinkedOrderId || !orders.length) return;
    const hit = orders.find((o) => o.id === deepLinkedOrderId);
    if (hit) setSelected(hit);
  }, [deepLinkedOrderId, orders]);

  const customerName = (cid) =>
    customers.find((c) => c.id === cid)?.name || "—";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders
      .filter((o) => (filter === "all" ? true : o.status === filter))
      .filter((o) => {
        if (!q) return true;
        return (
          customerName(o.customer_id).toLowerCase().includes(q) ||
          (o.notes || "").toLowerCase().includes(q) ||
          (o.lines || []).some((l) =>
            (l.name || "").toLowerCase().includes(q),
          )
        );
      })
      .sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      );
  }, [orders, filter, search, customers]);

  const counts = useMemo(() => {
    const out = { all: orders.length };
    STATUSES.forEach((s) => {
      out[s.value] = orders.filter((o) => o.status === s.value).length;
    });
    return out;
  }, [orders]);

  return (
    <div className="space-y-4 hv-fade-in" data-testid="order-requests-page">
      {counts.pending_review > 0 && (
        <div
          className="rounded-lg border p-4 md:p-5 shadow-lg flex items-start gap-3 flex-wrap"
          style={{
            borderColor: "#F43F5E66",
            background: "linear-gradient(135deg, rgba(244,63,94,0.15) 0%, rgba(244,63,94,0.05) 60%, rgba(167,139,250,0.08) 100%)",
          }}
          data-testid="orders-priority-callout"
        >
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-md bg-rose-500/20 border border-rose-400/50 flex items-center justify-center">
              <AlertOctagon className="w-5 h-5 text-rose-300" />
            </div>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-500 ring-2 ring-[#0a0b0e] animate-ping" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-500 ring-2 ring-[#0a0b0e]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-lg md:text-xl font-bold text-rose-100">Instant priority</h2>
              <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/25 border border-rose-400/50 text-rose-100 font-bold">
                {counts.pending_review} new order{counts.pending_review === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-sm text-[#fcd9e0] mt-0.5">
              {counts.pending_review === 1
                ? "A customer placed an order via the portal. Review, approve, edit or reject below."
                : `${counts.pending_review} customers placed orders via the portal. Review, approve, edit or reject below.`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFilter("pending_review")}
            className="hv-btn hv-btn-primary text-xs shrink-0"
            data-testid="orders-priority-jump"
          >
            Show only new
          </button>
        </div>
      )}
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">
            Client portal · Quote requests
          </div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-[#A78BFA]" /> Order requests
          </h1>
        </div>
        <div className="text-xs text-[#647087]">
          Review and convert client orders into quotes / sales orders.
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={`hv-btn ${
            filter === "all" ? "hv-btn-primary" : "hv-btn-ghost"
          } text-xs`}
          data-testid="orders-filter-all"
        >
          <Filter className="w-3 h-3" /> All ({counts.all})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setFilter(s.value)}
            className={`hv-btn ${
              filter === s.value ? "hv-btn-primary" : "hv-btn-ghost"
            } text-xs`}
            data-testid={`orders-filter-${s.value}`}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: s.color }}
            />{" "}
            {s.label} ({counts[s.value] || 0})
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer / item…"
            className="hv-input hv-input-search pl-7 text-xs"
            data-testid="orders-search"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-[#647087] p-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">
          No orders match the current filter.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="orders-list">
          {filtered.map((o) => (
            <li
              key={o.id}
              className={`hv-card p-3 hover:border-[#A78BFA]/40 cursor-pointer transition ${
                o.status === "pending_review" ? "border-rose-400/40 bg-rose-500/5" : ""
              } ${isUnseen(o) ? "hv-unseen" : ""}`}
              onClick={() => { dismiss(o.id); setSelected(o); }}
              data-testid={`order-row-${o.id}`}
              data-focus-id={o.id}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <UnseenDot unseen={isUnseen(o)} testId={`order-unseen-${o.id}`} />
                    <Link
                      to={`/customers/${o.customer_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-sm hover:underline"
                    >
                      {customerName(o.customer_id)}
                    </Link>
                    <StatusPill status={o.status} />
                    {o.preferred_date && (
                      <span className="text-[10px] font-mono text-[#1FB6C1] border border-[#1FB6C1]/30 bg-[#1FB6C1]/10 rounded-full px-2 py-0.5">
                        Needed {new Date(o.preferred_date).toLocaleDateString('en-GB')}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#647087] mt-1">
                    {(o.lines || []).length} item(s) ·{" "}
                    {o.total_quantity || (o.lines || []).reduce((s, l) => s + (l.quantity || 0), 0)}{" "}
                    units · submitted{" "}
                    {o.created_at ? new Date(o.created_at).toLocaleString('en-GB') : ""}{" "}
                    {o.submitted_by_name ? `by ${o.submitted_by_name}` : ""}
                  </div>
                  {o.notes && (
                    <div className="text-[11px] text-[#a0aabf] mt-1 italic line-clamp-2">
                      “{o.notes}”
                    </div>
                  )}
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#647087]" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => {
            setSelected(null);
            // Clean URL if we were deep-linked.
            if (deepLinkedOrderId) navigate("/order-requests", { replace: true });
          }}
          onChanged={async () => {
            await load();
            setSelected(null);
            if (deepLinkedOrderId) navigate("/order-requests", { replace: true });
          }}
          customerName={customerName}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUSES.find((x) => x.value === status) || {
    color: "#647087",
    label: status || "—",
  };
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border"
      style={{
        borderColor: `${s.color}55`,
        color: s.color,
        background: `${s.color}11`,
      }}
    >
      {s.label}
    </span>
  );
}

function OrderDetailModal({ order, onClose, onChanged, customerName, canEdit }) {
  const [status, setStatus] = useState(order.status);
  const [reviewNotes, setReviewNotes] = useState(order.review_notes || "");
  const [linkedQuoteId, setLinkedQuoteId] = useState(order.linked_quote_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const orderShortId = order.short_id || order.id?.slice(0, 8) || "order";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: `/order-requests/${order.id}/pdf`,
    filename: `Order_${orderShortId}.pdf`,
  });

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.patch(`/order-requests/${order.id}`, {
        status,
        review_notes: reviewNotes,
        linked_quote_id: linkedQuoteId || null,
      });
      onChanged();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Order from ${customerName(order.customer_id)}`}
      testId="order-detail-modal"
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="order-detail-download-pdf"
            title="Download a server-rendered PDF — always light mode, deterministic layout"
          >
            <Download className="w-3 h-3" />
            {downloadingPdf ? "Generating…" : "Download PDF"}
          </button>
          {canEdit && (
            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="hv-btn hv-btn-ghost text-xs"
              >
                <XIcon className="w-3 h-3" /> Close
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="order-detail-save"
              >
                <Check className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={order.status} />
          {order?.source_chat_thread_id && (
            <FromChatPill
              threadId={order.source_chat_thread_id}
              messageId={order.source_chat_message_id}
            />
          )}
          {order.preferred_date && (
            <span className="text-[10px] font-mono text-[#1FB6C1]">
              Needed by {new Date(order.preferred_date).toLocaleDateString('en-GB')}
            </span>
          )}
          <Link
            to={`/customers/${order.customer_id}`}
            className="hv-btn hv-btn-ghost text-[10px] ml-auto"
          >
            <ArrowUpRight className="w-3 h-3" /> Open customer
          </Link>
        </div>

        {/* Lines */}
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1.5">
            Items requested
          </h4>
          <ul className="space-y-1.5">
            {(order.lines || []).map((l, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 px-2 py-1.5 rounded border border-white/10 bg-white/[0.02]"
              >
                <Package className="w-3.5 h-3.5 text-[#647087] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{l.name}</div>
                  <div className="text-[10px] text-[#647087] truncate">
                    {l.sku || "—"} · qty {l.quantity} {l.unit}
                  </div>
                  {l.notes && (
                    <div className="text-[10px] italic text-[#a0aabf] mt-0.5">
                      {l.notes}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {order.notes && (
          <div className="text-xs italic text-[#a0aabf] border-l-2 border-[#A78BFA]/40 pl-3">
            “{order.notes}”
          </div>
        )}

        {canEdit && (
          <>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="hv-input w-full text-xs"
                data-testid="order-detail-status"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">
                Review notes
              </label>
              <VoiceTextarea
                value={reviewNotes}
                onChange={setReviewNotes}
                rows={4}
                className="w-full text-xs"
                placeholder="Internal notes for the team / customer reply… or tap the mic."
                context="lead_activity"
                testIdPrefix="order-detail-notes-voice"
                data-testid="order-detail-notes"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">
                Linked Quote ID (optional)
              </label>
              <input
                type="text"
                value={linkedQuoteId}
                onChange={(e) => setLinkedQuoteId(e.target.value)}
                className="hv-input w-full text-xs"
                placeholder="sales document id"
                data-testid="order-detail-linked-quote"
              />
            </div>
          </>
        )}

        {err && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}
